<template>
  <card-canvas class="company-detail">
    <loading-placeholder v-if="isLoading" :lines="10" />
    <template v-else>
      <card-canvas-two-deep-breadcrumb
        :parent-route="RouteAssistant.getPeopleRoute($route.params.accountId)"
        child-name="Company"
        parent-name="Users" />
      <!-- More dropdown -->
      <more-button-dropdown v-if="noPeopleInCompany && !$screen.isMobile($mq)" class="more-dropdown" placement="bottom-end">
        <template #options-list>
          <ul>
            <li class="scary-list-option" @click="deleteCompany">
              <i class="ddi ddi-trash" />
              <span>Delete Company</span>
            </li>
          </ul>
        </template>
      </more-button-dropdown>

      <!-- Company Name -->
      <div class="company-name__wrapper">
        <masthead-input
          v-model="companyName"
          :class="{'company-name__input--focus': focusName }"
          class="company-name__input"
          placeholder="Enter Company Name..."
          maxlength="50"
          @focus="focusName = true"
          @blur="focusName = false"
          @input-submitted="updateName"
        />
      </div>
      <people-roster
        :company="company"
        class="company-detail__people-roster"
        @account-memberships-updated="fetchCompanyRoster"
        @person-deleted="fetchCompanyRoster"
        @account-role-update-disallowed="fetchCompanyRoster"
      />
    </template>
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import CardCanvas from '@/layouts/card-canvas.vue'

/* Mixins */

/* Components */
import PeopleRoster from './components/people-roster.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { ICompanyRoster } from '@/api/types/response.types'

/* API */
import CompaniesAPI from '@/api/modules/companies.api'

export default defineComponent({
  name: 'CompanyCreate',

  components: {
    CardCanvas,
    PeopleRoster,
  },

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      focusName: false,
      companyName: '',
      RouteAssistant,
      company: {} as ICompanyRoster,
    }
  },

  head(): any {
    return {
      title: this.company.name,
    }
  },

  computed: {
    companyID(): number {
      return Number(this.$route.params.companyId)
    },
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    noPeopleInCompany(): boolean {
      return this.company.numberOfPeople === 0
    },
  },

  async created() {
    this.isLoading = true
    await this.fetchCompanyRoster()
    this.isLoading = false
  },

  methods: {
    focusNameField(): void {
      this.focusName = false
      this.$nextTick(() => {
        this.focusName = true
      })
    },
    async fetchCompanyRoster() {
      try {
        this.company = await CompaniesAPI.get_Roster(this.companyID)
        this.companyName = this.company.name
      } catch (error) {
        this.$error.emit(error)
        RouteAssistant.goToPeople(this.$router, this.accountID)
      }
    },
    async updateName() {
      if (
        this.companyName.trim().length === 0 ||
        this.companyName.trim() === this.company.name.trim()
      ) {
        this.companyName = this.company.name
        return
      }

      // Checked outside the try: its finally would clear the flag that a save still in flight set
      if (this.isSubmitting) {
        return
      }

      try {
        this.isSubmitting = true

        const response = await CompaniesAPI.put_UpdateName(
          this.companyID,
          this.companyName,
        )
        this.$messaging.showSuccess(response.message)
        this.company.name = this.companyName
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
    async deleteCompany() {
      try {
        const response = await CompaniesAPI.delete_Delete(this.companyID)
        this.$messaging.showSuccess(response.message)
        RouteAssistant.goToPeople(this.$router, this.accountID)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
  },
})
</script>

<style lang="scss">
.company-detail {
  .company-name {
    &__wrapper {
      margin-bottom: 4px;
    }

    &__input {
      width: calc(100% + 30px);
      margin-left: -15px;
      border: 6px solid transparent;

      > div {
        padding: 8px;

        .el-textarea__inner {
          padding: 0;
          border: transparent;
          box-shadow: none;

          @include media-breakpoint-down(sm) {
            font-size: 28px;
          }
        }
      }

      @include media-breakpoint-up(md) {
        &:hover {
          background-color: var(--color-background-secondary);
          border: 6px solid $color--gray-15;
          border-radius: 8px;
        }
      }

      &--focus {
        padding: 4px 0;
        width: calc(100% + 22px);
        margin-top: 4px;
        margin-bottom: 4px;
        margin-left: -11px;
        border: solid 2px $color--blue;
        border-radius: 8px;

        &:hover {
          background: white;
          border: solid 2px $color--blue;
          border-radius: 8px;
        }
      }
    }
  }

  .more-dropdown {
    position: absolute;
    right: 30px;
    top: 30px;
  }

  &__people-roster {
    border-top: solid 2px $color--gray-15;
  }
}
</style>
